import React from 'react';
import { User, Briefcase, GraduationCap, FileText } from 'lucide-react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfo,
  ResumeSection,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 18 - 모던한 2열 레이아웃 디자인
const Template18 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);
  
  return (
    <div className={`w-full max-w-4xl mx-auto bg-[#faf8f5] ${language === 'en' ? 'resume-english' : ''}`} style={{ fontFamily: "'SUIT Variable', 'Pretendard Variable', 'SUIT', 'Pretendard', -apple-system, sans-serif" }}>
      {/* Web Fonts Link */}
      <link rel="stylesheet" as="style" crossorigin href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css" />
      <link href="https://cdn.jsdelivr.net/gh/sunn-us/SUIT/fonts/variable/woff2/SUIT-Variable.css" rel="stylesheet" />
      <link href="https://webfontworld.github.io/gmarket/GmarketSans.css" rel="stylesheet" />
      <div className="bg-white shadow-lg border-2 border-[#e5ddd5]">
        {/* 헤더 섹션 */}
        <header>
          {/* 헤더 상단 - 이름과 직함 */}
          <div className="bg-[#5A5A6B] px-10 py-6 md:py-8">
            <h1 className={getPlaceholderClass(hasRealData.name, "text-4xl md:text-5xl font-bold mb-2 tracking-[0.3em] text-white", "text-4xl md:text-5xl font-bold mb-2 tracking-[0.3em] text-gray-400 italic")} style={{ fontFamily: "'Gmarket Sans', 'SUIT Variable', sans-serif", fontWeight: 700 }}>
              {currentData.name}
            </h1>
            <p className={getPlaceholderClass(hasRealData.title, "text-lg font-light text-white/90", "text-lg font-light text-gray-400 italic")} style={{ fontWeight: 300 }}>
              {currentData.title}
            </p>
          </div>
          
          {/* 헤더 하단 - 연락처 정보 */}
          <div className="bg-[#7A7A8B] px-8 py-4">
            <ContactInfo
              phone={currentData.phone}
              email={currentData.email}
              address={currentData.address}
              socialLinks={currentData.socialLinks}
              hasRealData={hasRealData}
              direction="horizontal"
              className="flex flex-wrap justify-center md:justify-around items-center gap-4 text-white text-sm"
              iconSize="w-4 h-4"
              textColor="text-white"
            />
          </div>
        </header>
        
        {/* 소개 섹션 */}
        {currentData.summary && (
          <section className="px-8 py-6 bg-[#faf8f5] border-b border-[#e5ddd5]">
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm text-gray-600 leading-relaxed max-w-3xl mx-auto text-center"
            />
          </section>
        )}
        
        {/* 메인 콘텐츠 - 2열 레이아웃 */}
        <main className="flex flex-col md:flex-row">
          {/* 왼쪽 컬럼 */}
          <aside className="md:w-2/5 md:border-r border-[#e5ddd5] bg-[#faf8f5]">
            {/* 핵심 역량 섹션 */}
            {currentData.keyStrengths.length > 0 && (
              <section className="border-b border-[#e5ddd5]">
                <div className="bg-[#faf8f5] px-8 py-2 border-b border-[#e5ddd5]">
                  <h2 className="text-xl font-bold text-[#6B6B7B]">{titles.coreCompetencies}</h2>
                </div>
                <div className="px-8 py-6">
                  <CoreCompetenciesSection
                    keyStrengths={currentData.keyStrengths}
                    hasRealData={hasRealData}
                    className="space-y-3"
                    bulletStyle="•"
                    bulletClassName="text-[#6B6B7B]"
                    textClassName="text-sm text-gray-700 leading-relaxed"
                  />
                </div>
              </section>
            )}
            
            {/* 학력 섹션 */}
            <section className="border-b border-[#e5ddd5]">
              <div className="bg-[#faf8f5] px-8 py-2 border-b border-[#e5ddd5]">
                <h2 className="text-xl font-bold text-[#6B6B7B]">{titles.education}</h2>
              </div>
              <div className="px-8 py-6">
                <EducationSection
                  education={currentData.education}
                  hasRealData={hasRealData}
                  datePosition="top"
                  degreeClassName="text-base font-medium text-gray-700"
                  schoolClassName="text-base text-gray-700 mb-1"
                  majorClassName="text-sm text-gray-600"
                  dateClassName="font-bold text-gray-800 mb-1 text-sm"
                  showMajor={true}
                  itemClassName="mb-6 last:mb-0"
                />
              </div>
            </section>
            
            {/* 보유 기술 섹션 */}
            <section className="border-b border-[#e5ddd5]">
              <div className="bg-[#faf8f5] px-8 py-2 border-b border-[#e5ddd5]">
                <h2 className="text-xl font-bold text-[#6B6B7B]">{titles.skills}</h2>
              </div>
              <div className="px-8 py-6">
                <SkillSection
                  skills={currentData.skills}
                  hasRealData={hasRealData}
                  variant="list"
                  bulletStyle="•"
                  itemClassName="text-sm"
                  className="space-y-2"
                  bulletClassName="text-[#6B6B7B]"
                  textClassName="text-gray-700"
                />
              </div>
            </section>
            
            {/* Languages Section (if language data exists) */}
            {currentData.languages && currentData.languages.length > 0 && (
              <section className="border-b border-[#e5ddd5]">
                <div className="bg-[#faf8f5] px-8 py-2 border-b border-[#e5ddd5]">
                  <h2 className="text-xl font-bold text-[#6B6B7B]">{titles.languages ? titles.languages.toUpperCase() : 'LANGUAGES'}</h2>
                </div>
                <div className="px-8 py-6">
                  <div className="space-y-2 text-sm">
                    {currentData.languages.map((lang, index) => (
                      <div key={index}>
                        <span className="font-medium">{lang.language}</span>
                        <span className="text-gray-500 ml-2">({lang.level})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
            
            {/* 자격증 섹션 */}
            {currentData.certificates.length > 0 && (
              <section>
                <div className="bg-[#faf8f5] px-8 py-2 border-b border-[#e5ddd5]">
                  <h2 className="text-xl font-bold text-[#6B6B7B]">{titles.certificates}</h2>
                </div>
              <div className="px-8 py-6">
                <CertificateSection
                  certificates={currentData.certificates}
                  hasRealData={hasRealData}
                  className="space-y-3 text-sm"
                  nameClassName="font-medium text-gray-700"
                  issuerClassName="text-xs text-gray-500"
                  dateClassName="text-xs text-gray-500"
                  dateFormat="separate"
                />
              </div>
              </section>
            )}
          </aside>
          
          {/* 오른쪽 컬럼 - 경력사항 */}
          <section className="flex-1 bg-[#faf8f5]">
            <div className="bg-[#faf8f5] px-8 py-2 border-b border-[#e5ddd5]">
              <h2 className="text-xl font-bold text-[#6B6B7B]">{titles.experience}</h2>
            </div>
            <div className="px-8 py-6">
              <ExperienceSection
                experiences={currentData.experiences}
                hasRealData={hasRealData}
                datePosition="top"
                companyClassName="text-base font-medium text-gray-700 mb-2"
                positionClassName="text-sm text-gray-600"
                dateClassName="text-sm font-bold text-gray-800 mb-2"
                descriptionClassName="text-sm text-gray-600 mb-4 leading-relaxed mt-2"
                projectTitleClassName="text-sm font-bold text-gray-700"
                projectDescriptionClassName="text-sm text-gray-600 mt-1"
                projectIndent="pl-4"
                itemClassName="mb-8 last:mb-0"
              />
            </div>
            
            {/* Papers Section */}
            {hasRealData.papers && (
              <div className="mt-8">
                <div className="bg-[#faf8f5] px-8 py-2 border-b border-[#e5ddd5]">
                  <h2 className="text-xl font-bold text-[#6B6B7B]">{titles.papers}</h2>
                </div>
                <div className="px-8 py-6">
                  <PaperSection
                    papers={currentData.papers}
                    hasRealData={hasRealData}
                    getPaperTypeText={getPaperTypeText}
                    titleClassName="text-base font-semibold text-gray-800"
                    authorsClassName="text-sm text-gray-600"
                    journalClassName="text-sm text-gray-700"
                    typeClassName="ml-2 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded"
                  />
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default Template18;